import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { INutritionInfo } from '@rbi-ctg/menu';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  ItemNutritionInfoToHide,
  defaultItemNutritionInfoToHide,
} from 'state/launchdarkly/variations';
import { TLocalizationKey } from 'types/i18n';
import { RBIBrand, brand } from 'utils/environment';
import { getUnitForNutrient } from 'utils/menu/units';

import { ProductSize } from '../product-size';
import { ItemTitleHeader, SubSectionTitleHeader } from '../styled';
import { StyledAdditionalInfo } from '../styled/additional-information';
import { StyledAllergens } from '../styled/allergens';
import { ContentWrapper } from '../styled/content-wrapper';
import { ItemName } from '../styled/item-name';
import { Label } from '../styled/label';
import { Picture } from '../styled/picture';
import { Table, TableData, TableHead, TableRow, TableTitle } from '../styled/table';
import { FALLBACK_NUTRITION_VALUE } from '../utils';
import { Nutrition } from '../v2/nutrition';

import { IItemNutritionProps } from './types';

const nutritionIdsEu = [
  'weight',
  'calories',
  'carbohydrates',
  'sugar',
  'fat',
  'saturatedFat',
  'proteins',
  'salt',
] as const satisfies ReadonlyArray<keyof INutritionInfo>;

const nutritionIdsUs = [
  'weight',
  'calories',
  'fat',
  'saturatedFat',
  'transFat',
  'cholesterol',
  'sodium',
  'carbohydrates',
  'fiber',
  'sugar',
  'proteins',
] as const satisfies ReadonlyArray<keyof INutritionInfo>;

type TNutritionKeyEu = (typeof nutritionIdsEu)[number];
type TNutritionKeyUs = (typeof nutritionIdsUs)[number];
export type TNutritionKey = TNutritionKeyEu | TNutritionKeyUs;

const prefixedKeyMapEu: Record<TNutritionKeyEu, TLocalizationKey> = {
  weight: 'eu-weight',
  calories: 'eu-calories',
  carbohydrates: 'eu-carbohydrates',
  sugar: 'eu-sugar',
  fat: 'eu-fat',
  saturatedFat: 'eu-saturatedFat',
  proteins: 'eu-proteins',
  salt: 'eu-salt',
};

const prefixedKeyMapUs: Record<TNutritionKeyUs, TLocalizationKey> = {
  weight: 'us-weight',
  calories: 'us-calories',
  fat: 'us-fat',
  saturatedFat: 'us-saturatedFat',
  transFat: 'us-transFat',
  cholesterol: 'us-cholesterol',
  sodium: 'us-sodium',
  carbohydrates: 'us-carbohydrates',
  fiber: 'us-fiber',
  sugar: 'us-sugar',
  proteins: 'us-proteins',
};

export const ItemNutrition: React.FC<IItemNutritionProps> = ({
  allergenGroups,
  itemImage,
  itemName,
  nutritionInfo,
  productSize,
  enableFeatureNutritionIntegration,
  enableStaticMenuAllergens,
  additionalItemInformation,
}) => {
  const { formatNumber, formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const formatJoules = useFormatJoules();

  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const itemsFromNutritionInfoToHide =
    useFlag<ItemNutritionInfoToHide>(LaunchDarklyFlag.HIDE_ITEMS_FROM_NUTRITION_INFO) ||
    defaultItemNutritionInfoToHide;
  const enableGermanLegalRequirement = useFlag(LaunchDarklyFlag.ENABLE_GERMAN_LEGAL_REQUIREMENT);
  const isBKBrand = brand() === RBIBrand.BK;

  const nutritionIds = useMemo(
    () => (enableEUNutritionDisplay ? nutritionIdsEu : nutritionIdsUs),
    [enableEUNutritionDisplay]
  );

  function getNutritionFallbackValue(nutritionKey: string) {
    if (nutritionKey === 'weight') {
      return `100 ${getUnitForNutrient(nutritionKey as TNutritionKey)}`;
    }

    if (nutritionKey === 'calories') {
      return `0 kJ / 0 kcal`;
    }

    return `${FALLBACK_NUTRITION_VALUE}${getUnitForNutrient(nutritionKey as TNutritionKey)}`;
  }

  const nutritionTable = useMemo(() => {
    return (
      <div>
        <Table data-testid="nutrition-v1-table">
          <TableHead>
            <TableTitle>{formatMessage({ id: 'Nutrition' })}</TableTitle>
            <TableTitle>{formatMessage({ id: 'perServing' })}</TableTitle>
            <TableTitle>{formatMessage({ id: 'perOneHundredGOrMl' })}</TableTitle>
          </TableHead>
          {nutritionIds?.map(nutritionKey => {
            const nutritionValue = nutritionInfo[nutritionKey];
            const unitNutritionKey = `${nutritionKey}Per100`;
            const unitNutritionValue = nutritionInfo[unitNutritionKey as keyof INutritionInfo];

            const isValidNutritionValue =
              nutritionValue !== undefined && nutritionValue !== null && nutritionValue !== 0;

            const isValidUnitNutritionValue =
              unitNutritionValue !== undefined &&
              unitNutritionValue !== null &&
              unitNutritionValue !== 0;

            if (
              (nutritionKey === 'weight' && nutritionValue === 0) ||
              itemsFromNutritionInfoToHide[nutritionKey]
            ) {
              return null;
            }

            if (nutritionKey === 'calories' && enableEUNutritionDisplay) {
              return (
                <TableRow key={nutritionKey} data-testid="nutrition-calories-row">
                  <TableData>
                    <Label>{formatMessage({ id: prefixedKeyMapEu[nutritionKey] })}</Label>
                  </TableData>
                  <TableData>
                    {isValidNutritionValue ? (
                      <span>
                        {formatMessage(
                          { id: 'euNutritionValues' },
                          {
                            calories: formatCalories(nutritionValue),
                            joules: formatJoules(nutritionValue),
                          }
                        )}
                      </span>
                    ) : (
                      getNutritionFallbackValue(nutritionKey)
                    )}
                  </TableData>
                  {unitNutritionKey === 'caloriesPer100' && (
                    <TableData>
                      {isValidUnitNutritionValue ? (
                        <span>
                          {formatMessage(
                            { id: 'euNutritionValues' },
                            {
                              calories: formatCalories(unitNutritionValue),
                              joules: formatJoules(unitNutritionValue),
                            }
                          )}
                        </span>
                      ) : (
                        getNutritionFallbackValue(nutritionKey)
                      )}
                    </TableData>
                  )}
                </TableRow>
              );
            }

            return (
              <TableRow key={nutritionKey}>
                <TableData>
                  <Label>
                    {formatMessage({
                      id: enableEUNutritionDisplay
                        ? prefixedKeyMapEu[nutritionKey as TNutritionKeyEu]
                        : prefixedKeyMapUs[nutritionKey as TNutritionKeyUs],
                    })}
                  </Label>
                </TableData>
                <TableData>
                  {isValidNutritionValue ? (
                    <span>
                      {`${formatNumber(nutritionValue, {
                        maximumFractionDigits: 1,
                      })} ${getUnitForNutrient(nutritionKey)}`}
                    </span>
                  ) : (
                    getNutritionFallbackValue(nutritionKey)
                  )}
                </TableData>
                <TableData>
                  {isValidUnitNutritionValue ? (
                    <span>
                      {`${formatNumber(unitNutritionValue, {
                        maximumFractionDigits: 1,
                      })} ${getUnitForNutrient(nutritionKey)}`}
                    </span>
                  ) : (
                    getNutritionFallbackValue(nutritionKey)
                  )}
                </TableData>
              </TableRow>
            );
          })}
        </Table>
      </div>
    );
  }, [
    formatMessage,
    nutritionIds,
    nutritionInfo,
    itemsFromNutritionInfoToHide,
    enableEUNutritionDisplay,
    formatNumber,
    formatCalories,
    formatJoules,
  ]);

  const allergenInformation = (
    <>
      {allergenGroups && allergenGroups.length > 0 ? (
        <>
          <ItemTitleHeader data-testid="allergens-header" $isBKBrand={isBKBrand}>
            {formatMessage({ id: 'allergenInformation' })}
          </ItemTitleHeader>
          <StyledAllergens allergenGroups={allergenGroups || []} />
        </>
      ) : null}
    </>
  );

  const additionalInfoSection = (
    <>
      <SubSectionTitleHeader
        data-testid="additional-information-header"
        $isBKBrand={isBKBrand}
        $enableGermanLegalRequirement={enableGermanLegalRequirement}
      >
        {formatMessage({ id: 'additionalInfoTitle' })}
      </SubSectionTitleHeader>
      <StyledAdditionalInfo
        additionalInfo={additionalItemInformation}
        productSize={productSize}
        enableStaticMenuAllergens={enableStaticMenuAllergens}
        enableGermanLegalRequirement={enableGermanLegalRequirement}
        allergenInformation={allergenInformation}
      />
    </>
  );

  return (
    <>
      <Picture image={itemImage} alt="" objectFitContain />
      <ContentWrapper>
        <ItemName data-testid="product-name">{itemName}</ItemName>
        {!enableGermanLegalRequirement && <ProductSize size={productSize}></ProductSize>}
        {enableGermanLegalRequirement && additionalInfoSection}
        {enableFeatureNutritionIntegration ? (
          <Nutrition nutritionInfo={nutritionInfo} />
        ) : (
          <>{nutritionTable}</>
        )}
        {!enableGermanLegalRequirement && additionalItemInformation ? additionalInfoSection : null}
        {!enableGermanLegalRequirement && enableStaticMenuAllergens && allergenInformation}
      </ContentWrapper>
    </>
  );
};
